import { useEffect, useRef, useState } from 'react'
import { cx } from '../ui/cx'

interface NeatController {
  destroy: () => void
}

function readToken(name: string): string | null {
  if (typeof window === 'undefined') return null
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value === '' ? null : value
}

/** Probes on a separate canvas so the real one keeps a clean context. */
function webglAvailable(): boolean {
  try {
    const probe = document.createElement('canvas')
    return Boolean(probe.getContext('webgl2') ?? probe.getContext('webgl'))
  } catch {
    return false
  }
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

function readPalette(): readonly string[] {
  return ['--material-1', '--material-2', '--material-3', '--material-4']
    .map(readToken)
    .filter((value): value is string => value !== null)
}

function buildConfig(palette: readonly string[], reducedMotion: boolean) {
  return {
    colors: palette.map((color) => ({ color, enabled: true })),
    speed: reducedMotion ? 0 : 2,
    horizontalPressure: 3,
    verticalPressure: 4,
    waveFrequencyX: 2,
    waveFrequencyY: 2,
    waveAmplitude: 5,
    shadows: 4,
    highlights: 3,
    colorBrightness: 1,
    colorSaturation: 2,
    colorBlending: 6,
    cameraZoom: 1.05,
    grainScale: 3,
    grainIntensity: 0.06,
    grainSparsity: 0,
    grainSpeed: reducedMotion ? 0 : 0.4,
    resolution: 0.8,
    wireframe: false,
    flowEnabled: false,
    domainWarpEnabled: false,
    iridescenceEnabled: false,
    fresnelEnabled: false,
    bloomIntensity: 0,
    chromaticAberration: 0,
    vignetteIntensity: 0.12,
    vignetteRadius: 0.6,
    shapeType: 'plane' as const,
  }
}

export interface NeatSurfaceProps {
  readonly className?: string | undefined
}

export function NeatSurface({ className }: NeatSurfaceProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [flat, setFlat] = useState(() => !webglAvailable())

  useEffect(() => {
    const canvas = canvasRef.current
    if (canvas === null || flat) return

    let disposed = false
    let controller: NeatController | null = null

    void (async () => {
      try {
        const palette = readPalette()
        if (palette.length === 0) {
          setFlat(true)
          return
        }

        const { NeatGradient } = await import('@firecms/neat')
        if (disposed) return
        controller = new NeatGradient({
          ref: canvas,
          ...buildConfig(palette, prefersReducedMotion()),
        })
      } catch {
        if (!disposed) setFlat(true)
      }
    })()

    return () => {
      disposed = true
      controller?.destroy()
    }
  }, [flat])

  if (flat) {
    return (
      <div aria-hidden="true" data-material="flat" className={cx('bg-background', className)} />
    )
  }

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-material="neat"
      className={cx('block size-full bg-background', className)}
    />
  )
}
